import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PasswordInput from '../../components/PasswordInput';
import srcFleetclearLogo from '../../assets/images/fleetclear-logo.svg';
import { Button, notification } from 'antd';
import { Form } from '@ant-design/compatible';
import { fetchApi } from "../../core/utils/api";
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';

import './index.scss';

interface UserSetPasswordProps {
    form: WrappedFormUtils;
}

const UserSetPassword: React.FC<UserSetPasswordProps> = ({ form }) => {
    const {token} = useParams<{ token: string }>();
    const history = useHistory();
    const [loading, setLoading] = useState<boolean>(true);
    
    const { getFieldDecorator, getFieldValue, getFieldError } = form;
    const newPassword = getFieldValue('password');
    const newPasswordError = getFieldError('password');
    const confirmNewPassword = getFieldValue('password_confirmation');
    const onFormSubmit = (e) => {
        e.preventDefault();
        const genericError = () => {
            notification.error({
                message: 'Error',
                description: 'Failed to set password'
            });
            throw new Error('Failed to set password');
        }
        form.validateFields((err, values) => {
            fetchApi({
                url: `set-password/${token}`,
                method: 'POST',
                body: values,
            }).then((response) => {
                if (response.data.success) {
                    notification.success({
                        message: 'Success',
                        description: 'Password has been set successfully, please login to continue.',
                    });
                    history.push('/login');
                    
                } else {
                    if (response.data.error) {
                        notification.error(response.data.error);
                        throw new Error(response.data.error);
                        
                    } else {
                        genericError();
                    }
                }
            }).catch(() => {
                genericError();
            });
        });
    };

    useEffect(() => {
        setLoading(false);
    }, []);

    return (
        <Layout
            data-test="pages-user-set-password"
            className="user-set-password"
            title="Set Password"
            isVisibleHeader={false}
            showFooter={false}
            loading={loading}
        >
            <div className="loginform-wrap">
                <div className="loginform-container">
                    <div className="d-flex dir-column align-center text-center">
                        <img 
                            src={srcFleetclearLogo} 
                            className="login__logo-fleet" 
                            alt="Fleetclear" 
                            style={{ width: '80%' }} 
                        />
                        <br />
                        <br />
                    </div>
                    <Form 
                        onSubmit={onFormSubmit}
                        layout="vertical" 
                        className="form mt-3"
                    >
                        <Form.Item>
                            {getFieldDecorator('password', {
                                rules: [
                                    {
                                        required: true,
                                        message: 'Please enter password!',
                                    },
                                    {
                                        min: 8,
                                        message: 'The password must be at least 8 characters length!\n\r',
                                    },
                                    {
                                        pattern: /\d+/,
                                        message: 'Must contain at least 1 digit\n\r',
                                    },
                                    {
                                        pattern: /[A-Z]+/,
                                        message: 'Must contain at least 1 uppercase character\n\r',
                                    },
                                    {
                                        pattern: /[a-z]+/,
                                        message: 'Must contain at least 1 lowercase character\n\r',
                                    },
                                    {
                                        pattern: /[!#$@%\-_=+<>\'"`£^&¬\.*\(\)\[\]\?:;\/,{}]/,
                                        message: 'Must contain at least 1 special character',
                                    },
                                ],
                            })(
                                <PasswordInput 
                                    placeholder="Enter New Password" 
                                    className="login-password" 
                                />
                            )}
                        </Form.Item>
                        <Form.Item>
                            {getFieldDecorator('password_confirmation', {
                                rules: [
                                    {
                                        required: true,
                                        message: 'Please confirm your new password!',
                                    },
                                ],
                            })(
                                <PasswordInput 
                                    placeholder="Confirm New Password" 
                                    className="login-password" 
                                />
                            )}
                        </Form.Item>
                        {confirmNewPassword && newPassword !== confirmNewPassword && (
                            <p style={{ 
                                color: '#ff4d4f', // same as antd validation
                                fontSize: '12px',
                                marginTop: '-16px',
                            }}>
                                Passwords do not match!
                            </p>
                        )}
                        <div className="text-center mt-2">
                            <Button 
                                type="primary" 
                                htmlType='submit'
                                disabled={!newPassword || !confirmNewPassword || (confirmNewPassword && newPassword !== confirmNewPassword) || newPasswordError}
                            >
                                Submit
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </Layout>
    );
};

const UserSetPasswordForm = Form.create({ name: 'userSetPassword' })(UserSetPassword);

export default UserSetPasswordForm;
